/** @prettier */

import {useEffect, useState} from 'react';
import {Grid, Card, CardContent, Stack, Box} from '@mui/material';
import {useLocation, useParams, Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import GroupAddDialog from './GroupAddDialog';
import GroupDeleteDialog from './GroupDeleteDialog';
import GroupEditDialog from './GroupEditDialog';
import CardContents from '../../component/CardContents';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {useOutlet} from '../floor-registration/FloorMain';
import {iconBlack} from '../../define';

/**
 * グループ一覧
 * @returns JSX
 */
const GroupRegistration = () => {
  const trans = useTranslation().t; //翻訳関数
  //親コンポーネントから値を取得
  const {floorName, allGroupInfo, setAllGroupInfo} = useOutlet();
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {floorID} = useParams();

  const [registeredGroup, setRegisteredGroup] = useState(false); //登録グループの有無
  //prettier-ignore
  const [floorGroupInfo, setFloorGroupInfo] = useState<apiIF.resParameters.groupInformations[] | null>(null); //表示されているフロアのグループの情報
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ表示フラグ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ表示フラグ
  const [selectedGroupID, setSelectedGroupID] = useState(''); //選択したグループのID

  /** グループ追加ボタンの名前 */
  const addButtonName = trans('GroupRegistration.buttonTxt.addGroup');

  //SL通信設定の空調機が登録されているグループのみを取得
  useEffect(() => {
    if (allGroupInfo === null) {
      return;
    }

    setRegisteredGroup(false);
    //表示している階層のグループの情報を抽出
    const tempGroupInfo = allGroupInfo['group-informations'].filter(
      groupInfo =>
        groupInfo['unit-system'] === 'SL' && groupInfo.floor === floorID
    );
    //階層にひとつ以上のグループが存在しない場合は終了
    if (tempGroupInfo.length === 0) {
      return;
    }
    setRegisteredGroup(true);
    setFloorGroupInfo(tempGroupInfo);
  }, [allGroupInfo, floorID]);

  /**
   * Editボタン押下時処理
   * @param groupID グループID
   */
  const openEditDialog = (groupID: string) => {
    setIsOpenEditDialog(true);
    setSelectedGroupID(groupID);
  };
  /**
   * Deleteボタン押下時処理
   * @param groupID グループID
   */
  const openDeleteDialog = (groupID: string) => {
    setIsOpenDeleteDialog(true);
    setSelectedGroupID(groupID);
  };

  /**
   * 歯車メニューリスト
   * @param groupID グループID
   * @returns メニュー一覧
   */
  const gearMenuItem = (groupID: string): GearMenuListArgType['menuItem'] => {
    return [
      {
        id: `mv-groupEdit${groupID}`,
        label: trans('GroupRegistration.menuItem.edit'),
        onClick: () => openEditDialog(groupID),
        icon: {
          className: 'groupSetting__contents__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(
            `GroupRegistration.image.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      },
      {
        id: `mv-groupDelete${groupID}`,
        label: trans('GroupRegistration.menuItem.delete'),
        onClick: () => openDeleteDialog(groupID),
        icon: {
          className: 'groupSetting__contents__button__icon',
          src: iconBlack.DELETE.src,
          alt: trans(
            `GroupRegistration.image.alternativeText.${iconBlack.DELETE.alt}`
          ),
        },
      },
    ];
  };

  return (
    <>
      {/* フロアの名前 */}
      <Box className="groupSetting__title">
        {floorName == null ? '' : floorName}
      </Box>
      <Stack spacing={4}>
        {/* グループ追加ボタン */}
        <Box className="groupSetting__contents--group groupSetting__contents__button--add">
          <GroupAddDialog
            addButtonName={addButtonName}
            unitSystem="SL"
            setGroupInfo={setAllGroupInfo}
            groupInfoData={allGroupInfo}
          />
        </Box>
        {/** 登録済グループが一つも無い場合は、グループ追加ボタンのみ表示 */}
        {floorGroupInfo === null || registeredGroup === false ? (
          <Card className="groupSetting__contents--group">
            <CardContent
              className="groupSetting__contents__message--noGroup"
              id="mv-noRegist-group">
              {trans('GroupRegistration.instruction', {name: addButtonName})}
            </CardContent>
          </Card>
        ) : (
          //登録済グループ
          floorGroupInfo.map(groupInfo => (
            <Card
              id={`floorGroupBox-${groupInfo['group-id']}`}
              key={groupInfo['group-id']}
              className="groupSetting__contents--group">
              <CardContent>
                <Stack spacing={2}>
                  {/* グループ名称と歯車 */}
                  <Grid
                    container
                    justifyContent="space-between"
                    className="groupSetting__contents__title">
                    {/* グループ名称 */}
                    <Grid item xs="auto">
                      <Link
                        to={`${location.pathname}/${groupInfo['group-id']}/group-layout-registration${location.search}`}
                        id={`mv-linkGpSetting${groupInfo['group-name']}`}>
                        {groupInfo['group-name']}
                      </Link>
                    </Grid>
                    {/* 歯車ボタン */}
                    <Grid item xs={1}>
                      <GearMenu
                        id={`mv-btnGroupSettingOpen${groupInfo['group-id']}`}
                        menuItem={gearMenuItem(groupInfo['group-id'])}
                      />
                      {groupInfo['group-id'] === selectedGroupID &&
                      groupInfo['group-name'] !== undefined ? (
                        <>
                          <GroupEditDialog
                            groupID={groupInfo['group-id']}
                            displayGroupInfo={groupInfo}
                            setGroupInfo={setAllGroupInfo}
                            isOpenDialog={isOpenEditDialog}
                            setIsOpenDialog={setIsOpenEditDialog}
                          />
                          <GroupDeleteDialog
                            groupID={groupInfo['group-id']}
                            groupName={groupInfo['group-name']}
                            setGroupInfo={setAllGroupInfo}
                            isOpenDialog={isOpenDeleteDialog}
                            setIsOpenDialog={setIsOpenDeleteDialog}
                          />
                        </>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                  {/* グループに登録済の室内機合計数と室内機名称一覧 */}
                  <Stack
                    spacing={2}
                    className="groupSetting__contents__contents">
                    {/* グループに登録済の室内機合計数 */}
                    <CardContents
                      class="groupSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'GroupRegistration.item.title.numberOfUnits'
                      )}
                      value={
                        groupInfo['registrations-number'] === undefined
                          ? ''
                          : groupInfo['registrations-number']
                      }
                    />
                    {/* グループに登録済の室内機名称一覧 */}
                    <CardContents
                      class="groupSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'GroupRegistration.item.title.indoorUnit'
                      )}
                      value={
                        groupInfo['unit-informations'] === undefined ? (
                          <></>
                        ) : (
                          <Stack spacing={0}>
                            {groupInfo['unit-informations'].map(iuInfo => (
                              <Box key={iuInfo['unit-id']}>
                                {iuInfo['unit-name']}
                              </Box>
                            ))}
                          </Stack>
                        )
                      }
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))
        )}
      </Stack>
    </>
  );
};
export default GroupRegistration;
